// ─────────────────────────────────────────────────────────────────────────────
// Lucro e fecho do ano.
//
// Responde a duas perguntas que nao existiam em ecra nenhum:
//   1. Quanto lucro fiz este ano, e com quanto fecho a 31/12?
//   2. Se o ano fosse INTEIRO a este ritmo, quanto seria? — que e a pergunta
//      que decide o regime de contabilidade, porque o negocio arrancou a meio
//      do ano e a facturacao de 2026 nao diz nada sobre 2027.
//
// TRES FONTES, TODAS MEDIDAS, NENHUMA INVENTADA:
//
//   Margem B2B   compras_fornecedor_encomendas — receita das encomendas menos
//                o custo alocado de cada factura. A conta interna sai das duas
//                pontas: ela compra ao custo, entrava com margem zero e so
//                diluia a percentagem.
//
//   Lucro B2C    produto_stock_snapshots — cada descida do stock diario e uma
//                venda. Mede-se assim e nao pelos lancamentos porque os
//                lancamentos chegam semanas depois, quando o marketplace
//                liberta o dinheiro — e a 06/09/2026 a `vendas_marketplace`
//                estava simplesmente vazia.
//
//   Despesas     movimentos_manuais, as recorrentes expandidas pelo ano, mais
//                uma estimativa configuravel para o que nunca chega a ser
//                lancado. Sao valores pequenos ao pe do resto, e uma estimativa
//                explicita vale mais do que um zero silencioso.
//
// TUDO SEM IVA. E o que e tributado e e a unica base em que os canais se podem
// somar: as compras sao intracomunitarias (autoliquidacao, ja vem sem IVA) e do
// lado do cliente tira-se, porque esse IVA nunca foi dinheiro nosso.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from './supabaseClient'
import { C } from './theme'
import { lerTudo } from './lerTudo'
import { Download, RefreshCw, AlertTriangle, Info } from 'lucide-react'

const fmt = (n) => Number(n || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmt0 = (n) => Math.round(Number(n || 0)).toLocaleString('pt-PT')
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

// Guardados no browser, como o limite do regime que ja existia nesta pagina.
const LS = 'fecho_ano_params_v1'
const DEFEITO = {
  lucroPorUnidade: 20,      // EUR limpos por unidade vendida no marketplace
  recebidoPorUnidade: 100,  // EUR facturados por unidade — o valor RECEBIDO
  despesasEstimadas: 175,   // EUR/mes que nao passam pelos movimentos manuais
  crescimento: 0,           // % a somar ao ritmo medido, so na parte projectada
}

export default function FechoAno({ ano, limite }) {
  const [params, setParams] = useState(() => {
    try { return { ...DEFEITO, ...JSON.parse(localStorage.getItem(LS) || '{}') } } catch { return DEFEITO }
  })
  useEffect(() => { localStorage.setItem(LS, JSON.stringify(params)) }, [params])

  const [compras, setCompras] = useState([])
  const [links, setLinks] = useState([])
  const [encomendas, setEncomendas] = useState([])
  const [snapshots, setSnapshots] = useState([])
  const [manuais, setManuais] = useState([])
  const [servicos, setServicos] = useState([])
  const [sage, setSage] = useState([])
  const [internos, setInternos] = useState({ ids: new Set(), emails: new Set() })
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState('')

  const carregar = async () => {
    setLoading(true); setErro('')
    try {
      const de = `${ano}-01-01`, ate = `${ano}-12-31`
      const { data: perfis } = await supabase.from('perfis').select('id, email').eq('cliente_interno', true)
      const ids = new Set((perfis || []).map(p => p.id))
      const emails = new Set((perfis || []).map(p => String(p.email || '').toLowerCase()).filter(Boolean))
      setInternos({ ids, emails })

      const [cRes, lRes, eRes, sRes, mRes, svRes, fdRes] = await Promise.all([
        supabase.from('compras_fornecedor')
          .select('id, numero_fatura, numero_interno, data_fatura, estado, fase, valor_total, valor_final_total')
          .gte('data_fatura', de).lte('data_fatura', ate).order('data_fatura'),
        lerTudo(() => supabase.from('compras_fornecedor_encomendas')
          .select('compra_id, encomenda_id, quantidade_alocada, custo_unitario_final, custo_unitario_previsao'), 'compra_id'),
        lerTudo(() => supabase.from('encomendas')
          .select('id, cliente_id, estado, total_sem_iva, desconto_valor'), 'id'),
        // So os artigos da conta interna: sao os unicos que ele revende no
        // marketplace. O resto do stock e de clientes e nao e venda dele.
        lerTudo(() => supabase.from('produto_stock_snapshots')
          .select('produto_nome, cliente_email, snapshot_date, stock_lognow')
          .gte('snapshot_date', de).lte('snapshot_date', ate), 'snapshot_date'),
        supabase.from('movimentos_manuais').select('*'),
        // Servicos prestados — o terceiro canal. Nao tem custo de mercadoria:
        // o valor facturado e todo margem.
        supabase.from('vendas_marketplace')
          .select('valor_faturado, valor_total, data, produto_nome')
          .eq('tipo', 'servico').gte('data', de).lte('data', ate),
        // A FACTURACAO REAL. O Sage tem tudo o que foi facturado; a plataforma
        // so conhece o que passou por ela. Em 2026 a diferenca eram 20.213 EUR
        // — quase um terco — porque o negocio comecou em B2C e so mais tarde
        // teve encomendas B2B lancadas aqui.
        supabase.from('faturacao_declarada').select('*').eq('ano', Number(ano)).order('mes'),
      ])
      if (cRes.error) throw new Error('Compras: ' + cRes.error.message)
      setCompras(cRes.data || [])
      setLinks(lRes.data || [])
      setEncomendas(eRes.data || [])
      setSnapshots((sRes.data || []).filter(s => emails.has(String(s.cliente_email || '').toLowerCase())))
      setManuais(mRes.data || [])
      setServicos(svRes.data || [])
      setSage(fdRes.data || [])
    } catch (e) { setErro(e.message) }
    setLoading(false)
  }
  useEffect(() => { carregar() }, [ano])

  const d = useMemo(() => {
    const encPorId = new Map(encomendas.map(e => [e.id, e]))
    const ehInterna = (encId) => {
      const e = encPorId.get(encId)
      return !e || internos.ids.has(e.cliente_id)
    }

    // ── 1. Margem B2B, factura a factura ───────────────────────────────────
    const porCompra = new Map()
    for (const l of links) {
      if (ehInterna(l.encomenda_id)) continue
      const e = encPorId.get(l.encomenda_id)
      if (!e || String(e.estado || '').startsWith('cancel')) continue
      const a = porCompra.get(l.compra_id) || { custo: 0, encIds: new Set(), unidades: 0 }
      a.custo += Number(l.custo_unitario_final ?? l.custo_unitario_previsao ?? 0) * Number(l.quantidade_alocada || 0)
      a.unidades += Number(l.quantidade_alocada || 0)
      a.encIds.add(l.encomenda_id)
      porCompra.set(l.compra_id, a)
    }
    // Unidades que foram para a conta interna — nao dao margem B2B nenhuma, mas
    // sao a medida do reinvestimento e vale a pena ve-las.
    const idsDoAno = new Set(compras.map(c => c.id))
    const unidadesInternas = links.reduce((s, l) =>
      s + (idsDoAno.has(l.compra_id) && ehInterna(l.encomenda_id) ? Number(l.quantidade_alocada || 0) : 0), 0)

    const facturas = compras.map(c => {
      const a = porCompra.get(c.id) || { custo: 0, encIds: new Set(), unidades: 0 }
      // total_sem_iva ja vem sem IVA da origem; o desconto abate-se por cima.
      let receita = 0
      for (const id of a.encIds) {
        const e = encPorId.get(id)
        if (e) receita += Number(e.total_sem_iva || 0) - Number(e.desconto_valor || 0)
      }
      receita = Math.round(receita * 100) / 100
      const custo = Math.round(a.custo * 100) / 100
      return {
        id: c.id, numero: c.numero_fatura || c.numero_interno, data: c.data_fatura,
        receita, custo, unidades: a.unidades,
        margem: Math.round((receita - custo) * 100) / 100,
        pct: receita > 0 ? ((receita - custo) / receita * 100) : 0,
      }
    }).filter(f => f.receita > 0 || f.custo > 0)

    const margemB2B = Math.round(facturas.reduce((s, f) => s + f.margem, 0) * 100) / 100
    const vendasB2B = Math.round(facturas.reduce((s, f) => s + f.receita, 0) * 100) / 100

    // ── 2. Vendas B2C, medidas pelo stock ──────────────────────────────────
    //
    // Cada descida no stock diario e uma venda; as subidas sao entregas e
    // ignoram-se. Agrupa-se por (artigo, email) porque o mesmo artigo existe
    // uma vez por cliente na tabela Produtos.
    const series = new Map()
    for (const s of snapshots) {
      const k = s.produto_nome + ' ' + s.cliente_email
      if (!series.has(k)) series.set(k, [])
      series.get(k).push(s)
    }
    const porProduto = new Map()
    const unidadesPorMes = new Map()
    let diaMin = null, diaMax = null
    for (const [, arr] of series) {
      arr.sort((a, b) => String(a.snapshot_date).localeCompare(String(b.snapshot_date)))
      let ant = null
      for (const s of arr) {
        if (s.stock_lognow == null) continue
        const cur = Number(s.stock_lognow)
        if (ant != null && cur < ant) {
          porProduto.set(s.produto_nome, (porProduto.get(s.produto_nome) || 0) + (ant - cur))
          const m = new Date(String(s.snapshot_date) + 'T00:00:00').getMonth()
          unidadesPorMes.set(m, (unidadesPorMes.get(m) || 0) + (ant - cur))
        }
        ant = cur
        if (!diaMin || s.snapshot_date < diaMin) diaMin = s.snapshot_date
        if (!diaMax || s.snapshot_date > diaMax) diaMax = s.snapshot_date
      }
    }
    const produtosB2C = [...porProduto.entries()]
      .map(([nome, un]) => ({ nome, un })).filter(p => p.un > 0)
      .sort((a, b) => b.un - a.un)
    const unidadesB2C = produtosB2C.reduce((s, p) => s + p.un, 0)
    const lucroB2C = Math.round(unidadesB2C * Number(params.lucroPorUnidade || 0) * 100) / 100
    const vendasB2C = Math.round(unidadesB2C * Number(params.recebidoPorUnidade || 0) * 100) / 100
    const diasMedidos = (diaMin && diaMax)
      ? Math.max(1, Math.round((new Date(diaMax) - new Date(diaMin)) / 86400000) + 1) : 0
    const porDia = diasMedidos > 0 ? unidadesB2C / diasMedidos : 0

    // ── 3. Despesas ────────────────────────────────────────────────────────
    //
    // As recorrentes contam-se pelos meses do ano em que estao activas; as
    // pontuais so se a data cair no ano.
    const mesesDoAno = (m) => {
      const inicio = new Date(String(m.data) + 'T00:00:00')
      const fim = m.ate ? new Date(String(m.ate) + 'T00:00:00') : new Date(Number(ano), 11, 31)
      let n = 0
      for (let mes = 0; mes < 12; mes++) {
        const dia = new Date(Number(ano), mes, 15)
        if (dia >= new Date(inicio.getFullYear(), inicio.getMonth(), 1) && dia <= fim) n++
      }
      return n
    }
    // ── Servicos prestados ─────────────────────────────────────────────────
    const vendasServicos = Math.round(servicos.reduce(
      (s, v) => s + Number(v.valor_faturado ?? v.valor_total ?? 0), 0) * 100) / 100
    // Sem custo de mercadoria: o facturado e todo margem.
    const lucroServicos = vendasServicos

    let despesasLancadas = 0
    for (const m of manuais) {
      if (!m.activo || m.tipo !== 'saida') continue
      // O levantamento do titular mexe na conta mas nao e custo — e o lucro a
      // ser retirado. Conta-lo como despesa fazia o lucro aparecer a menos.
      if (m.conta_no_lucro === false) continue
      const v = Number(m.valor || 0)
      if (m.recorrencia === 'mensal') despesasLancadas += v * mesesDoAno(m)
      else if (m.recorrencia === 'trimestral') despesasLancadas += v * Math.ceil(mesesDoAno(m) / 3)
      else if (m.recorrencia === 'anual') despesasLancadas += v * (mesesDoAno(m) > 0 ? 1 : 0)
      else if (String(m.data || '').slice(0, 4) === String(ano)) despesasLancadas += v
    }
    despesasLancadas = Math.round(despesasLancadas * 100) / 100

    // ── 3b. Facturacao: manda o Sage onde ele existe ───────────────────────
    //
    // A margem continua a vir da plataforma — o Sage nao tem custos. Mas a
    // FACTURACAO e a dele: tem tudo, incluindo o que nunca passou pelo site.
    // Nos meses que ele cobre nao se soma nada por cima, senao o B2C entrava
    // duas vezes (as facturas do marketplace ja estao la dentro).
    const platPorMes = Array(12).fill(0)
    for (const fa of facturas) {
      if (!fa.data) continue
      platPorMes[new Date(fa.data).getMonth()] += fa.receita
    }
    for (const v of servicos) {
      if (!v.data) continue
      platPorMes[new Date(v.data).getMonth()] += Number(v.valor_faturado ?? v.valor_total ?? 0)
    }
    for (const [m, un] of unidadesPorMes) {
      platPorMes[m] += un * Number(params.recebidoPorUnidade || 0)
    }
    const sagePorMes = new Map(sage.map(s => [Number(s.mes) - 1, Number(s.valor_sem_iva || 0)]))
    const ivaSage = sage.reduce((s, x) => s + (Number(x.valor_com_iva || 0) - Number(x.valor_sem_iva || 0)), 0)
    const facturacaoPorMes = Array.from({ length: 12 }, (_, m) =>
      sagePorMes.has(m) ? sagePorMes.get(m) : Math.round(platPorMes[m] * 100) / 100)
    const facturacaoMedida = Math.round(facturacaoPorMes.reduce((s, v) => s + v, 0) * 100) / 100

    // ── 4. Meses activos, para anualizar ───────────────────────────────────
    //
    // O negocio arrancou a meio do ano. Dividir pelos 12 meses do calendario
    // dava uma media que nunca existiu — mede-se da primeira factura ate hoje.
    const hoje = new Date()
    const noAnoCorrente = hoje.getFullYear() === Number(ano)
    const primeira = facturas.length ? new Date(facturas[0].data) : null
    const fimJanela = noAnoCorrente ? hoje : new Date(Number(ano), 11, 31)
    const mesesActivos = primeira ? Math.max(1, (fimJanela - primeira) / (86400000 * 30.44)) : 0
    const mesesRestantes = noAnoCorrente
      ? Math.max(0, (new Date(Number(ano), 11, 31) - hoje) / (86400000 * 30.44)) : 0

    const margemB2BMes = mesesActivos > 0 ? margemB2B / mesesActivos : 0
    // OS SERVICOS MEDEM-SE NOS MESES EM QUE EXISTEM, nao desde a primeira
    // factura de compra.
    //
    // Sao facturados uma vez por mes e comecaram agora. Dividi-los pelos meses
    // do B2B dava 178 EUR/mes quando o valor real e ~680 — e projectava 675 EUR
    // ate ao fim do ano em vez de 2.600. Um canal que arranca a meio nao se
    // dilui pelo tempo em que nao existia.
    const mesesComServicos = new Set(
      servicos.filter(v => v.data).map(v => String(v.data).slice(0, 7))).size
    const servicosMes = mesesComServicos > 0 ? vendasServicos / mesesComServicos : 0
    const vendasB2BMes = mesesActivos > 0 ? vendasB2B / mesesActivos : 0
    const despesasMes = mesesActivos > 0 ? despesasLancadas / mesesActivos : 0
    const outrasMes = Number(params.despesasEstimadas || 0)

    // ── 5. Fecho do ano ────────────────────────────────────────────────────
    // O crescimento so mexe no que FALTA. O que ja aconteceu esta medido e nao
    // se corrige com percentagens; sem esta separacao, mudar o campo mexia no
    // passado e o ecra deixava de servir para conferir seja o que for.
    const g = 1 + Number(params.crescimento || 0) / 100
    const projB2B = Math.round(margemB2BMes * mesesRestantes * g * 100) / 100
    const projServicos = Math.round(servicosMes * mesesRestantes * 100) / 100
    const projB2C = Math.round(porDia * (mesesRestantes * 30.44) * g * Number(params.lucroPorUnidade || 0) * 100) / 100
    const outrasAteAgora = Math.round(outrasMes * mesesActivos * 100) / 100
    const despesasFuturas = Math.round((despesasMes + outrasMes) * mesesRestantes * 100) / 100
    const lucroAteAgora = Math.round((margemB2B + lucroB2C + lucroServicos - despesasLancadas - outrasAteAgora) * 100) / 100
    const lucroAno = Math.round((lucroAteAgora + projB2B + projB2C + projServicos - despesasFuturas) * 100) / 100

    // ── Ritmo recente, medido nos MESES COMPLETOS ──────────────────────────
    //
    // A media do ano inteiro nao serve: o negocio triplicou de trimestre para
    // trimestre e Janeiro pesava tanto como Agosto. Tres meses completos e o
    // compromisso — dois davam 180 mil e tres dao 147 mil, e a diferenca entre
    // os dois e so a variacao natural de Julho para Agosto.
    const mesAtual = noAnoCorrente ? hoje.getMonth() : 12
    const completos = []
    for (let m = mesAtual - 1; m >= 0 && completos.length < 3; m--) {
      if (facturacaoPorMes[m] > 0) completos.push({ m, v: facturacaoPorMes[m] })
    }
    const ritmoMes = completos.length > 0
      ? completos.reduce((s, x) => s + x.v, 0) / completos.length : 0

    const facturacaoAteAgora = facturacaoMedida
    const facturacaoAno = Math.round((facturacaoMedida + ritmoMes * mesesRestantes * g) * 100) / 100

    // ── 6. Ano inteiro a esta media ────────────────────────────────────────
    const anualLucro = Math.round((
      (margemB2BMes * 12) + (porDia * 365 * Number(params.lucroPorUnidade || 0))
      + (servicosMes * 12) - ((despesasMes + outrasMes) * 12)
    ) * 100) / 100
    const anualFacturacao = Math.round(ritmoMes * 12 * 100) / 100
    const margemRealPct = anualFacturacao > 0 ? anualLucro / anualFacturacao * 100 : 0

    return {
      facturas, margemB2B, vendasB2B, unidadesInternas,
      vendasServicos, lucroServicos, servicosMes, projServicos, nServicos: servicos.length,
      mesesComServicos,
      produtosB2C, unidadesB2C, lucroB2C, vendasB2C, diasMedidos, porDia,
      despesasLancadas, outrasMes, outrasAteAgora, despesasFuturas,
      mesesActivos, mesesRestantes, margemB2BMes, vendasB2BMes,
      projB2B, projB2C, lucroAteAgora, lucroAno,
      facturacaoAteAgora, facturacaoAno,
      facturacaoPorMes, sagePorMes, ivaSage: Math.round(ivaSage * 100) / 100,
      ritmoMes, mesesRitmo: completos.map(x => x.m), nSage: sage.length,
      anualLucro, anualFacturacao, margemRealPct,
    }
  }, [compras, links, encomendas, snapshots, manuais, servicos, sage, internos, params, ano])

  const exportar = () => {
    const linhas = [
      [`FECHO DO ANO ${ano} - valores SEM IVA`, '', ''],
      ['', '', ''],
      ['MEDIDO', 'EUR', 'nota'],
      ['Vendas B2B', d.vendasB2B.toFixed(2), `${d.facturas.length} factura(s) de fornecedor`],
      ['Margem B2B', d.margemB2B.toFixed(2), 'conta interna excluida'],
      ['Unidades vendidas B2C', String(d.unidadesB2C), 'medidas pelo stock diario'],
      ['Vendas B2C', d.vendasB2C.toFixed(2), `${params.recebidoPorUnidade} EUR/un recebidos`],
      ['Lucro B2C', d.lucroB2C.toFixed(2), `${params.lucroPorUnidade} EUR/un limpos`],
      ['Servicos prestados', d.vendasServicos.toFixed(2), 'sem custo de mercadoria - facturado = margem'],
      ['Despesas lancadas', d.despesasLancadas.toFixed(2), 'movimentos manuais'],
      ['Outras despesas estimadas', d.outrasAteAgora.toFixed(2), `${params.despesasEstimadas} EUR/mes`],
      ['Lucro ate agora', d.lucroAteAgora.toFixed(2), `${d.mesesActivos.toFixed(1)} meses medidos`],
      ['', '', ''],
      ['PROJECTADO ATE 31/12', 'EUR', ''],
      ['Margem B2B a acrescer', d.projB2B.toFixed(2), `${fmt(d.margemB2BMes)} EUR/mes`],
      ['Lucro B2C a acrescer', d.projB2C.toFixed(2), `${d.porDia.toFixed(2)} un/dia`],
      ['Despesas do periodo', d.despesasFuturas.toFixed(2), ''],
      ['LUCRO DO ANO', d.lucroAno.toFixed(2), ''],
      ['FACTURACAO DO ANO', d.facturacaoAno.toFixed(2), ''],
      ['', '', ''],
      ['FACTURACAO MES A MES', 'EUR', 'origem'],
      ...d.facturacaoPorMes.map((v, m) => v > 0
        ? [MESES[m], v.toFixed(2), d.sagePorMes.has(m) ? 'Sage' : 'plataforma']
        : null).filter(Boolean),
      ['IVA cobrado', d.ivaSage.toFixed(2), 'meses do Sage'],
      ['', '', ''],
      ['ANO INTEIRO A ESTE RITMO', 'EUR', 'para o regime de contabilidade'],
      ['Ritmo medido', d.ritmoMes.toFixed(2), `media de ${d.mesesRitmo.length} mes(es) completos`],
      ['Facturacao', d.anualFacturacao.toFixed(2), ''],
      ['Lucro', d.anualLucro.toFixed(2), ''],
      ['Margem real', `${d.margemRealPct.toFixed(1)}%`, ''],
      ['Limite de referencia', Number(limite).toFixed(2), `${(d.anualFacturacao / limite * 100).toFixed(0)}% do limite`],
    ]
    const csv = '﻿' + linhas.map(l => l.join(';')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
    const a = document.createElement('a')
    a.href = url; a.download = `fecho-ano-${ano}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  if (loading) return <div style={{ padding: '30px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>A medir…</div>
  if (erro) return <div style={{ padding: '10px 14px', background: C.dangerSoft, border: `1px solid ${C.danger}55`, borderRadius: '4px', color: C.danger, fontSize: '12px' }}>{erro}</div>

  const rot = { padding: '3px 0', color: C.textMuted, fontSize: '12px' }
  const num = { padding: '3px 0', textAlign: 'right', fontFamily: 'monospace', fontSize: '12.5px', fontWeight: '600', whiteSpace: 'nowrap' }
  const rotT = { ...rot, padding: '7px 0 2px', fontWeight: '800', color: C.text, borderTop: `1px solid ${C.border}` }
  const numT = { ...num, padding: '7px 0 2px', fontSize: '15px', fontWeight: '800', borderTop: `1px solid ${C.border}` }
  const caixa = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '14px 16px', marginBottom: '12px' }
  const titulo = { margin: '0 0 10px', fontSize: '11px', fontWeight: '800', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }
  const campo = { width: '90px', padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', textAlign: 'right' }

  const pctLimite = limite > 0 ? Math.min(100, d.anualFacturacao / limite * 100) : 0
  const passaLimite = d.anualFacturacao > limite

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <p style={{ margin: 0, fontSize: '12px', color: C.textMuted }}>
          Margem medida factura a factura, vendas B2C medidas pelo stock diário. <strong>Tudo sem IVA.</strong>
        </p>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={carregar} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 11px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
            <RefreshCw size={13} /> Actualizar
          </button>
          <button onClick={exportar} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 11px', border: 'none', borderRadius: '4px', background: C.primary, color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
            <Download size={13} /> CSV p/ contabilista
          </button>
        </div>
      </div>

      {/* Os tres numeros que esta pagina existe para dar. */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
        {[
          { t: 'Lucro até agora', v: d.lucroAteAgora, s: `${d.mesesActivos.toFixed(1)} meses medidos`, cor: C.success },
          { t: `Lucro estimado ${ano}`, v: d.lucroAno, s: `+ ${fmt(d.projB2B + d.projB2C)} € projectados`, cor: C.text },
          { t: `Facturação ${ano}`, v: d.facturacaoAno, s: d.nSage > 0 ? `${d.nSage} mês(es) do Sage · ${limite > 0 ? (d.facturacaoAno / limite * 100).toFixed(0) : 0}% do limite` : `${limite > 0 ? (d.facturacaoAno / limite * 100).toFixed(0) : 0}% do limite`, cor: C.text },
        ].map(k => (
          <div key={k.t} style={{ flex: '1 1 220px', ...caixa, marginBottom: 0 }}>
            <div style={titulo}>{k.t}</div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: k.cor, lineHeight: 1, fontFamily: 'monospace' }}>{fmt(k.v)} €</div>
            <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>{k.s}</div>
          </div>
        ))}
      </div>

      {/* O que e medido e o que e assumido — separados de proposito. */}
      <div style={{ ...caixa, background: C.surfaceAlt }}>
        <div style={titulo}>O que é assumido</div>
        <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {[
            { k: 'lucroPorUnidade', l: 'Lucro por unidade B2C', h: '€ limpos, já com portes e comissões' },
            { k: 'recebidoPorUnidade', l: 'Recebido por unidade B2C', h: '€ facturados — o valor que entra' },
            { k: 'despesasEstimadas', l: 'Outras despesas', h: '€/mês fora dos movimentos manuais' },
            { k: 'crescimento', l: 'Crescimento até 31/12', h: '% sobre o ritmo medido — época alta, encomenda maior' },
          ].map(c => (
            <div key={c.k}>
              <label style={{ display: 'block', fontSize: '10.5px', fontWeight: '700', color: C.textMuted, marginBottom: '3px' }}>{c.l}</label>
              <input type="number" step="0.01" value={params[c.k]}
                onChange={e => setParams(p => ({ ...p, [c.k]: e.target.value === '' ? '' : Number(e.target.value) }))}
                style={campo} />
              <div style={{ fontSize: '10px', color: C.textFaint, marginTop: '2px', maxWidth: '190px' }}>{c.h}</div>
            </div>
          ))}
        </div>
        <p style={{ margin: '10px 0 0', fontSize: '11px', color: C.textMuted, lineHeight: 1.5 }}>
          Tudo o resto nesta página é medido. Estes quatro são os únicos números assumidos — mexe neles
          e vê o efeito. As despesas lançadas na Tesouraria ({fmt(d.despesasLancadas)} € este ano) contam à parte,
          e o crescimento só se aplica ao que <strong>falta</strong> do ano: o que já aconteceu está medido
          e não se corrige com percentagens.
        </p>
      </div>

      <div style={caixa}>
        <div style={titulo}>Como se chega ao lucro de {ano}</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr><td style={{ ...rot, fontWeight: '700', color: C.text }}>Medido</td><td style={num}></td></tr>
            <tr><td style={rot}>Margem B2B · {d.facturas.length} factura(s)</td><td style={{ ...num, color: C.success }}>{fmt(d.margemB2B)} €</td></tr>
            <tr><td style={rot}>Lucro B2C · {d.unidadesB2C} un vendidas</td><td style={{ ...num, color: C.success }}>{fmt(d.lucroB2C)} €</td></tr>
            {d.vendasServicos > 0 && (
              <tr><td style={rot}>Serviços prestados · {d.nServicos} factura(s)</td><td style={{ ...num, color: C.success }}>{fmt(d.lucroServicos)} €</td></tr>
            )}
            <tr><td style={rot}>Despesas lançadas</td><td style={{ ...num, color: C.textMuted }}>− {fmt(d.despesasLancadas)} €</td></tr>
            <tr><td style={rot}>Outras despesas · {fmt(d.outrasMes)} €/mês</td><td style={{ ...num, color: C.textMuted }}>− {fmt(d.outrasAteAgora)} €</td></tr>
            <tr><td style={rotT}>Lucro até agora</td><td style={numT}>{fmt(d.lucroAteAgora)} €</td></tr>

            <tr><td style={{ ...rot, fontWeight: '700', color: C.text, paddingTop: '14px' }}>
              Projectado · {d.mesesRestantes.toFixed(1)} meses até 31/12
              {Number(params.crescimento) > 0 && <span style={{ color: C.warning, fontWeight: '700' }}> · +{params.crescimento}% assumido</span>}
            </td><td style={num}></td></tr>
            <tr><td style={rot}>Margem B2B · {fmt(d.margemB2BMes)} €/mês</td><td style={num}>+ {fmt(d.projB2B)} €</td></tr>
            <tr><td style={rot}>Lucro B2C · {d.porDia.toFixed(2)} un/dia</td><td style={num}>+ {fmt(d.projB2C)} €</td></tr>
            {d.projServicos > 0 && (
              <tr><td style={rot}>
                Serviços · {fmt(d.servicosMes)} €/mês
                <span style={{ fontSize: '10px' }}> (medido em {d.mesesComServicos} mês{d.mesesComServicos === 1 ? '' : 'es'})</span>
              </td><td style={num}>+ {fmt(d.projServicos)} €</td></tr>
            )}
            <tr><td style={rot}>Despesas do período</td><td style={{ ...num, color: C.textMuted }}>− {fmt(d.despesasFuturas)} €</td></tr>
            <tr><td style={rotT}>Lucro de {ano}</td><td style={{ ...numT, color: C.success }}>{fmt(d.lucroAno)} €</td></tr>
          </tbody>
        </table>
      </div>

      <div style={{ ...caixa, border: `2px solid ${passaLimite ? C.warning : C.border}`, background: passaLimite ? C.warningSoft : C.surface }}>
        <div style={{ ...titulo, color: passaLimite ? '#92400e' : C.textMuted }}>Se o ano fosse inteiro a esta média</div>
        <p style={{ margin: '0 0 10px', fontSize: '11.5px', color: C.textMuted, lineHeight: 1.5 }}>
          O negócio arrancou a meio do ano e triplicou de trimestre para trimestre — a média do ano
          inteiro não diz nada sobre o ano seguinte. Isto extrapola o <strong>ritmo dos últimos
          {d.mesesRitmo.length} meses completos</strong> ({d.mesesRitmo.map(m => MESES[m]).reverse().join(', ')})
          para doze, que é a conta que decide o regime de contabilidade.
        </p>
        <p style={{ margin: '0 0 10px', padding: '8px 10px', background: C.surfaceAlt, borderRadius: '3px', fontSize: '11.5px', color: C.textMuted }}>
          Ritmo medido: <strong style={{ color: C.text }}>{fmt(d.ritmoMes)} €/mês</strong>.
          Um mês forte e um mês fraco mudam muito este número — olha-o como ordem de grandeza, não como previsão.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
          <tbody>
            <tr><td style={rot}>Facturação de um ano completo</td><td style={{ ...num, fontSize: '15px', fontWeight: '800' }}>{fmt(d.anualFacturacao)} €</td></tr>
            <tr><td style={rot}>Lucro de um ano completo</td><td style={{ ...num, fontSize: '15px', fontWeight: '800' }}>{fmt(d.anualLucro)} €</td></tr>
            <tr><td style={rot}>Margem real</td><td style={num}>{d.margemRealPct.toFixed(1)}%</td></tr>
            <tr><td style={rot}>Limite de referência</td><td style={num}>{fmt0(limite)} €</td></tr>
          </tbody>
        </table>
        <div style={{ height: '10px', background: C.surfaceAlt, borderRadius: '5px', overflow: 'hidden', border: `1px solid ${C.border}` }}>
          <div style={{ width: `${pctLimite}%`, height: '100%', background: passaLimite ? C.warning : C.success }} />
        </div>
        <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>
          {limite > 0 ? (d.anualFacturacao / limite * 100).toFixed(0) : 0}% do limite
        </div>
        {passaLimite && (
          <p style={{ margin: '10px 0 0', padding: '9px 11px', background: C.surface, border: `1px solid ${C.warning}`, borderRadius: '4px', fontSize: '11.5px', color: '#92400e', lineHeight: 1.55 }}>
            <AlertTriangle size={12} style={{ verticalAlign: '-1px' }} /> <strong>Um ano completo a este ritmo passa o limite.</strong>{' '}
            Fala com a contabilista antes do fim do ano — as regras de saída do regime simplificado
            olham a anos consecutivos e a margens de excesso, e o que houver a decidir decide-se
            antes do ano começar, não em Dezembro.
          </p>
        )}
        <p style={{ margin: '10px 0 0', fontSize: '11px', color: C.textMuted, lineHeight: 1.55 }}>
          <Info size={11} style={{ verticalAlign: '-1px' }} /> A margem real ({d.margemRealPct.toFixed(1)}%) é o número
          a levar à conversa: quanto mais perto estiver do coeficiente que o regime simplificado aplica à venda
          de mercadoria, menos diferença faz mudar de regime.
        </p>
      </div>

      <div style={caixa}>
        <div style={titulo}>
          Facturação mês a mês
          {d.nSage > 0 && <span style={{ marginLeft: '6px', fontSize: '9px', fontWeight: '800', color: '#5b21b6', background: '#ede9fe', border: '1px solid #5b21b644', borderRadius: '8px', padding: '1px 6px' }}>SAGE MANDA</span>}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <tbody>
              {d.facturacaoPorMes.map((v, m) => v > 0 && (
                <tr key={m} style={{ borderTop: `1px solid ${C.border}`, background: d.mesesRitmo.includes(m) ? C.surfaceAlt : 'transparent' }}>
                  <td style={{ padding: '4px 0', width: '60px', fontWeight: '700' }}>{MESES[m]}</td>
                  <td style={{ padding: '4px 0', color: C.textMuted, fontSize: '10px' }}>
                    {d.sagePorMes.has(m) ? 'Sage' : 'plataforma'}
                    {d.mesesRitmo.includes(m) && <span style={{ color: C.primary, fontWeight: '700' }}> · conta para o ritmo</span>}
                  </td>
                  <td style={{ padding: '4px 0', textAlign: 'right', fontFamily: 'monospace', fontWeight: '600', whiteSpace: 'nowrap' }}>{fmt(v)} €</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: `2px solid ${C.border}`, fontWeight: '800' }}>
                <td style={{ padding: '6px 0' }} colSpan="2">Facturado até agora</td>
                <td style={{ padding: '6px 0', textAlign: 'right', fontFamily: 'monospace', fontSize: '14px' }}>{fmt(d.facturacaoAteAgora)} €</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p style={{ margin: '8px 0 0', fontSize: '11px', color: C.textMuted, lineHeight: 1.55 }}>
          Nos meses importados do Sage manda o Sage — ele tem tudo o que foi facturado, incluindo o que
          nunca passou por aqui. Nesses meses <strong>não se soma o B2C por cima</strong>: as tuas facturas
          do marketplace já lá estão dentro. A margem é que continua a vir da plataforma, porque só ela tem os custos.
          {d.ivaSage > 0 && <> IVA cobrado no ano: <strong>{fmt(d.ivaSage)} €</strong>.</>}
        </p>
      </div>

      <div style={caixa}>
        <div style={titulo}>Margem B2B · factura a factura</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: C.surfaceAlt }}>
                {['Factura', 'Data', 'Un', 'Vendas s/IVA', 'Custo', 'Margem', '%'].map((h, i) => (
                  <th key={h} style={{ padding: '6px 8px', textAlign: i > 1 ? 'right' : 'left', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.facturas.map(f => (
                <tr key={f.id} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={{ padding: '5px 8px', fontWeight: '700' }}>{f.numero}</td>
                  <td style={{ padding: '5px 8px', color: C.textMuted }}>{f.data ? new Date(f.data).toLocaleDateString('pt-PT') : '—'}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', color: C.textMuted }}>{f.unidades}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(f.receita)}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace', color: C.textMuted }}>{fmt(f.custo)}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', color: C.success }}>{fmt(f.margem)}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: '700', color: f.pct < 12 ? C.warning : C.textMuted }}>{f.pct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: `2px solid ${C.border}`, background: C.surfaceAlt, fontWeight: '800' }}>
                <td style={{ padding: '7px 8px' }} colSpan="3">Total</td>
                <td style={{ padding: '7px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(d.vendasB2B)}</td>
                <td style={{ padding: '7px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(d.vendasB2B - d.margemB2B)}</td>
                <td style={{ padding: '7px 8px', textAlign: 'right', fontFamily: 'monospace', color: C.success }}>{fmt(d.margemB2B)}</td>
                <td style={{ padding: '7px 8px', textAlign: 'right' }}>{d.vendasB2B > 0 ? (d.margemB2B / d.vendasB2B * 100).toFixed(1) : '0.0'}%</td>
              </tr>
            </tfoot>
          </table>
        </div>
        {d.unidadesInternas > 0 && (
          <p style={{ margin: '8px 0 0', fontSize: '11px', color: C.textMuted, lineHeight: 1.5 }}>
            A conta interna está fora destes números nas duas pontas — ela compra ao custo e daria margem zero.
            Foram <strong>{d.unidadesInternas} unidades</strong> para ela este ano: é o lucro B2B reinvestido,
            e volta como lucro B2C quando sair no marketplace.
          </p>
        )}
      </div>

      {d.vendasServicos > 0 && (
        <div style={caixa}>
          <div style={titulo}>Serviços prestados</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr><td style={rot}>Facturado este ano · {d.nServicos} factura(s)</td><td style={{ ...num, fontSize: '15px', fontWeight: '800' }}>{fmt(d.vendasServicos)} €</td></tr>
              <tr><td style={rot}>Média por mês · {d.mesesComServicos} mês(es) com serviços</td><td style={num}>{fmt(d.servicosMes)} €</td></tr>
            </tbody>
          </table>
          <p style={{ margin: '9px 0 0', fontSize: '11px', color: C.textMuted, lineHeight: 1.55 }}>
            Um serviço não tem custo de mercadoria — o facturado é todo margem, e por isso entra
            no lucro pelo valor inteiro. O dinheiro que tiras para ti a seguir <strong>não é despesa</strong>:
            marca esse movimento na Tesouraria com &laquo;não é despesa da empresa&raquo; e ele sai da conta
            sem tirar nada ao lucro.
          </p>
        </div>
      )}

      <div style={caixa}>
        <div style={titulo}>Vendas B2C · medidas pelo stock diário</div>
        {d.unidadesB2C === 0 ? (
          <p style={{ margin: 0, fontSize: '12px', color: C.textMuted }}>
            Sem descidas de stock registadas em {ano} nos artigos da conta interna.
          </p>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <tbody>
                {d.produtosB2C.map(p => (
                  <tr key={p.nome} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ padding: '5px 0' }}>{p.nome}</td>
                    <td style={{ padding: '5px 0', textAlign: 'right', fontWeight: '700', whiteSpace: 'nowrap' }}>{p.un} un</td>
                    <td style={{ padding: '5px 0', textAlign: 'right', fontFamily: 'monospace', color: C.success, whiteSpace: 'nowrap' }}>
                      {fmt(p.un * Number(params.lucroPorUnidade || 0))} €
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ margin: '9px 0 0', fontSize: '11px', color: C.textMuted, lineHeight: 1.55 }}>
              <strong>{d.unidadesB2C} unidades</strong> em {d.diasMedidos} dias medidos = <strong>{d.porDia.toFixed(2)} un/dia</strong>.
              É o que foi vendido de facto. Se o sistema medir mais procura por dia do que isto,
              a diferença são dias em que estiveste sem stock.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
